import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ReactPaginate from 'react-paginate';
import { useAuth } from '../../context/AuthContext';
import withPermission from '../../utils/withPermission';
import Swal from 'sweetalert2';
import DomainForm from './DomainForm';
import DomainDetails from './DomainDetails';

const DomainList = () => {
    const [domains, setDomains] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedDomainId, setSelectedDomainId] = useState(null);
    const [selectedDomainData, setSelectedDomainData] = useState(null);
    const [showDomainDetails, setShowDomainDetails] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [errors, setErrors] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const debounceTimeoutRef = useRef(null);
    const [showDnsModal, setShowDnsModal] = useState(false);
    const { permissions } = useAuth();

    const fetchDomains = async (page = 1) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/domain`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params: {
                    page: page,
                    search: searchQuery,
                },
            });

            const { data, current_page, last_page } = response.data.Data;
            setDomains(data);
            setCurrentPage(current_page - 1);
            setPageCount(last_page);
        } catch (error) {
            console.error('Error fetching domains:', error);
        }
    };

    useEffect(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            fetchDomains(1);
        }, searchQuery ? 1500 : 0);

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const WithDeletePermission = withPermission(({ children }) => children, ['domain-delete']);
    const WithEditPermission = withPermission(({ children }) => children, ['domain-edit']);
    const WithCreatePermission = withPermission(({ children }) => children, ['domain-create']);

    const addDomain = async (newDomain) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.post(`${backendUrl}/api/domain`, newDomain, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });
            const createdDomain = response.data?.Data[0];

            if (createdDomain) {
                setDomains((prev) => [...prev, createdDomain]);
            }
            handleCloseModal();
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'Added successfully!',
                timer: 3000,
                showConfirmButton: false,
            });
        } catch (error) {
            if (error.response?.data?.errors) {
                return error.response.data.errors;
            }
            console.error('Error adding domain:', error);
        }
    };

    const editDomain = async (id, updatedData) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.put(`${backendUrl}/api/domain/${id}`, updatedData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });
            const updatedDomain = response.data?.Data;

            if (updatedDomain) {
                setDomains((prev) =>
                    prev.map((d) => (d.id === updatedDomain.id ? updatedDomain : d))
                );
            }
            handleCloseModal();
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'Updated successfully!',
                timer: 3000,
                showConfirmButton: false,
            });
        } catch (error) {
            if (error.response?.data?.errors) {
                return error.response.data.errors;
            }
            console.error('Error updating domain:', error);
        }
    };

    const deleteDomain = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL;
                    const userInfo = JSON.parse(localStorage.getItem('user-info'));
                    const origin = localStorage.getItem('user-origin');
                    const token = userInfo?.token;

                    await axios.delete(`${backendUrl}/api/domain/${id}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Tenant-Origin': origin,
                        },
                    });

                    setDomains(domains.filter(d => d.id !== id));
                    Swal.fire("Deleted!", "The domain has been deleted.", "success");
                } catch (error) {
                    console.error('Error deleting domain:', error);
                    Swal.fire("Error!", "Failed to delete domain.", "error");
                }
            }
        });
    };

    const handleShowModal = (id = null) => {
        if (id) {
            const domainToEdit = domains.find(d => d.id === id);
            if (domainToEdit) {
                setSelectedDomainData(domainToEdit);
            }
        } else {
            setSelectedDomainData(null);
        }
        setSelectedDomainId(id);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedDomainId(null);
        setSelectedDomainData(null);
        setErrors({});
    };

    const handlePageClick = (data) => {
        fetchDomains(data.selected + 1);
    };

    const handleShowDnsModal = (id) => {
        setSelectedDomainId(id);
        setShowDomainDetails(true);
    };

    const handleCloseDnsModal = () => {
        setShowDnsModal(false);
        setSelectedDomainId(null);
        setSelectedDomainData(null);
    };

    const handleShowDomainDetails = (id) => {
        setSelectedDomainId(id);
        setShowDomainDetails(true);
    };

    const handleCloseDomainDetails = () => {
        setShowDomainDetails(false);
        setSelectedDomainId(null);
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Domains</h4>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-3">
                                        <div className="search-box me-2 mb-2 d-inline-block">
                                            <div className="position-relative">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search..."
                                                    value={searchQuery}
                                                    onChange={handleSearchChange}
                                                />
                                                <i className="bx bx-search-alt search-icon" />
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <WithCreatePermission>
                                                <button
                                                    type="button"
                                                    className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                                                    onClick={() => handleShowModal()}
                                                    title='Add Domain'
                                                >
                                                    <i className="mdi mdi-plus"></i>
                                                </button>
                                            </WithCreatePermission>
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table align-middle table-nowrap table-check table-list">
                                            <thead className="table-sales-gradient">
                                                <tr>
                                                    <th style={{ width: '20px' }}>#</th>
                                                    <th>Domain Name</th>
                                                    <th>Zone ID</th>
                                                    <th>Provider</th>
                                                    <th>Remarks</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {domains.length > 0 ? (
                                                    domains.map((domain, index) => (
                                                        <tr key={domain.id}>
                                                            <td>{currentPage * 10 + index + 1}</td>
                                                            <td>
                                                                <button 
                                                                    className="btn btn-link p-0 text-decoration-none fw-semibold" 
                                                                    onClick={() => handleShowDomainDetails(domain.id)}
                                                                >
                                                                    {domain.domain_name}
                                                                </button>
                                                            </td>
                                                            <td>{domain.zoneid}</td>
                                                            <td>{domain.provider}</td>
                                                            <td>{domain.remarks}</td>
                                                            <td>
                                                                <div className="dropdown">
                                                                    <i className="mdi mdi-dots-horizontal font-size-18" data-bs-toggle="dropdown" style={{ cursor: 'pointer' }}></i>
                                                                    <ul className="dropdown-menu dropdown-menu-end">
                                                                        <li>
                                                                            <WithEditPermission>
                                                                                <button className="dropdown-item" onClick={() => handleShowModal(domain.id)}>Edit</button>
                                                                            </WithEditPermission>
                                                                        </li>
                                                                        <li>
                                                                            <button className="dropdown-item" onClick={() => handleShowDnsModal(domain.id)}>Manage DNS</button>
                                                                        </li>
                                                                        <li>
                                                                            <WithDeletePermission>
                                                                                <button className="dropdown-item text-danger" onClick={() => deleteDomain(domain.id)}>Delete</button>
                                                                            </WithDeletePermission>
                                                                        </li>
                                                                    </ul>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="6" className="text-center">No domains found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="d-flex justify-content-end pagination-sm">
                                        <ReactPaginate
                                            previousLabel={'<'}
                                            nextLabel={'>'}
                                            breakLabel={'...'}
                                            pageCount={pageCount}
                                            marginPagesDisplayed={2}
                                            pageRangeDisplayed={3}
                                            onPageChange={handlePageClick}
                                            containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                                            pageClassName={'page-item'}
                                            pageLinkClassName={'page-link'}
                                            previousClassName={'page-item previous'}
                                            previousLinkClassName={'page-link'}
                                            nextClassName={'page-item next'}
                                            nextLinkClassName={'page-link'}
                                            breakClassName={'page-item'}
                                            breakLinkClassName={'page-link'}
                                            activeClassName={'active'}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <DomainForm
                        showModal={showModal}
                        handleClose={handleCloseModal}
                        domainId={selectedDomainId}
                        domainData={selectedDomainData}
                        addDomain={addDomain}
                        editDomain={editDomain}
                        domains={domains}
                        errors={errors}
                        setErrors={setErrors}
                    />

                    <DomainDetails
                        show={showDomainDetails}
                        onHide={handleCloseDomainDetails}
                        domainId={selectedDomainId}
                    />
                </div>
            </div>
        </div>
    );
};

export default DomainList;
